import { useEffect, useState } from 'react';
import { Share2, X } from 'lucide-react';
import { useT } from '../i18n';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { MultiSelectFilter } from './MultiSelectFilter';

// "Partager" button + form on a project page: sends the project's link (on the
// stage shown) to colleagues as a Slack direct message and an e-mail (both
// ticked by default, either can be unticked) with an optional note
// (POST /projects/:id/share) — like a task's "Envoyer un rappel". Someone who
// couldn't open the project (private, not part of it) is skipped and named.
const ERROR_KEYS: Record<string, string> = {
  NO_RECIPIENT: 'share.err.noRecipient',
  NO_CHANNEL: 'tasks.remind.err.noChannel',
  NO_EMAIL: 'share.err.noEmail',
  NO_ACCESS: 'share.err.noAccess',
  SLACK_DISABLED: 'tasks.remind.err.slackDisabled',
  NOT_ON_SLACK: 'share.err.notOnSlack',
  SLACK_FAILED: 'tasks.remind.err.slackFailed',
  EMAIL_FAILED: 'tasks.remind.err.emailFailed',
};

const CHANNEL_KEYS: Record<string, string> = { slack: 'tasks.remind.viaSlack', email: 'tasks.remind.viaEmail' };

type Failure = { name: string; channel?: string; code: string };

export function ShareProject({ projectId, tab }: {
  projectId: number;
  /** The stage shown — the link opens the project on it. */
  tab?: string;
}) {
  const { t } = useT();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [people, setPeople] = useState<{ value: number; label: string }[]>([]);
  const [recipients, setRecipients] = useState<number[]>([]);
  const [note, setNote] = useState('');
  const [viaSlack, setViaSlack] = useState(true);
  const [viaEmail, setViaEmail] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || people.length) return;
    api.kanbanUsers()
      .then((users) => setPeople(users.filter((u) => u.id !== user?.id).map((u) => ({ value: u.id, label: u.name }))))
      .catch(() => {});
  }, [open]);

  const failureText = (f: Failure) => {
    const reason = ERROR_KEYS[f.code] ? t(ERROR_KEYS[f.code], { name: f.name }) : f.code;
    return f.channel
      ? t('share.channelFailed', { name: f.name, channel: t(CHANNEL_KEYS[f.channel]), reason })
      : t('share.skipped', { name: f.name, reason });
  };

  const close = () => { setOpen(false); setRecipients([]); setNote(''); };

  const send = async () => {
    setSending(true);
    try {
      const res = await api.shareProject(projectId, recipients, note.trim(), { slack: viaSlack, email: viaEmail }, tab);
      close();
      const lines = res.sent.map((s) => t('share.sent', { name: s.name, via: s.channels.map((c) => t(CHANNEL_KEYS[c])).join(t('tasks.remind.and')) }));
      alert([...lines, ...res.failed.map(failureText)].join('\n'));
    } catch (err: any) {
      const failures: Failure[] = err.details?.failed ?? [];
      alert(failures.length
        ? [t('tasks.remind.nothingSent'), ...failures.map(failureText)].join('\n')
        : ERROR_KEYS[err.code] ? t(ERROR_KEYS[err.code]) : err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button type="button" className="btn-icon" title={t('share.help')} aria-label={t('share.button')} onClick={() => setOpen(true)}>
        <Share2 size={14} />
      </button>
      {open && (
        <div className="modal-overlay" onClick={close}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <h3 className="modal-title">{t('share.title')}</h3>
              <button className="close-btn" onClick={close}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">{t('share.toLabel')}</label>
                <MultiSelectFilter label="" options={people} selected={recipients} onChange={setRecipients} placeholder={t('share.toPlaceholder')} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('tasks.remind.noteLabel')}</label>
                <textarea
                  className="form-input"
                  rows={4}
                  value={note}
                  placeholder={t('share.notePlaceholder')}
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">{t('tasks.remind.channelsLabel')}</label>
                <div style={{ display: 'flex', gap: 'var(--s16)' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--s8)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={viaSlack} onChange={(e) => setViaSlack(e.target.checked)} />
                    <span>{t('tasks.remind.slack')}</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--s8)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={viaEmail} onChange={(e) => setViaEmail(e.target.checked)} />
                    <span>{t('tasks.remind.email')}</span>
                  </label>
                </div>
              </div>
              <p className="text-sm text-ink-3" style={{ margin: 0 }}>{t('share.explain')}</p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={close}>{t('common.cancel')}</button>
              <button type="button" className="btn btn-accent" disabled={sending || !recipients.length || (!viaSlack && !viaEmail)} onClick={send}>{t('common.send')}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
